import { useEffect, useMemo, useState } from 'react';
import { Paperclip, RefreshCw, RotateCcw, Trash2, Upload } from 'lucide-react';

import { usePermission } from '../../../../auth/AuthContext';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { Tooltip } from '../../../../components/Tooltip';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { ApiError } from '../../../../lib/apiError';
import { saveBlob } from '../../../../lib/saveBlob';
import { useToast } from '../../../../components/toastContext';
import { personnelApi } from '../personnelApi';
import type { PersonnelDetail, PersonnelDocument } from '../personnelTypes';
import { personnelDocumentTypesApi } from '../../personnel-documents/personnelDocumentTypesApi';
import type { PersonnelDocumentType } from '../../personnel-documents/personnelDocumentTypes';
import { DocumentPreviewModal } from './DocumentPreviewModal';

/**
 * The documents a 201 file holds, read against the checklist.
 *
 * **The panel lists the CHECKLIST, not the files.** Every kind the organization asks for is a
 * row whether or not this employee has filed one, with the files stacked under it and an
 * Upload at the outer right. That is the shape a document checklist has on paper, and it is
 * what makes completeness readable: a kind nobody has filed is a row saying so, in its own
 * place in the checklist's order.
 *
 * It is the `<SurveyPanel>` shape for the same reason — a table of filed documents with a
 * Create button would start empty on every record and say nothing about what is outstanding.
 * **So there is no Create, no search and no pager**: nothing is created here, and a list
 * exactly as long as the checklist has nothing to page.
 *
 * **SEVERAL files may name one kind.** An employee holds one birth certificate and ten
 * appointments, so Upload adds rather than replaces, and each file carries its own Replace and
 * Delete — revealed on the line's hover or focus, so a checklist of filed papers reads as a list
 * of names rather than a column of trash cans.
 *
 * **A file's name OPENS it** in `<DocumentPreviewModal>` (27 Oct 2026, replacing download-on-click)
 * — the inert preview beside its file facts, with Download in the footer.
 */
export function DocumentsPanel({
  record,
  onUpload,
  onReplace,
  onChanged,
}: {
  record: PersonnelDetail;
  /** Opens the upload form for one checklist row. */
  onUpload: (documentType: PersonnelDocumentType) => void;
  /** Opens the same form to swap one filed document's file. */
  onReplace: (document: PersonnelDocument) => void;
  /** A document was removed or put back — the profile refetches. */
  onChanged: () => void;
}) {
  const toast = useToast();
  const canUpdate = usePermission('personnel.update');

  const [checklist, setChecklist] = useState<PersonnelDocumentType[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);

  /** The document a write is in flight for, so only its own control is busy. */
  const [busyId, setBusyId] = useState<string | null>(null);

  /** The document open in the preview. */
  const [previewing, setPreviewing] = useState<PersonnelDocument | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);

    personnelDocumentTypesApi
      .options(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setChecklist(next);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('Could not load the document checklist.', 0),
        );
      });

    return () => controller.abort();
  }, [reloadKey]);

  /*
    The documents this record holds, by checklist row. A deleted document is not one this
    record holds, so it is out unless the reader asks — the standing `showDeleted` shape,
    derived rather than a second fetch.
  */
  const byType = useMemo(() => {
    const groups = new Map<string, PersonnelDocument[]>();

    for (const document of record.documents) {
      if (!document.isActive && !showDeleted) continue;

      const filed = groups.get(document.personnelDocumentTypeId);

      if (filed) filed.push(document);
      else groups.set(document.personnelDocumentTypeId, [document]);
    }

    return groups;
  }, [record.documents, showDeleted]);

  const hasDeleted = record.documents.some((document) => !document.isActive);

  /*
    Rows a DELETED checklist kind still holds. They are not on the checklist any more, so the
    options call does not carry them — and dropping them would hide files the record genuinely
    holds. They sort after the live rows, which is where a retired heading belongs.
  */
  const retired = useMemo(() => {
    const live = new Set((checklist ?? []).map((type) => type.id));
    const seen = new Map<string, string>();

    for (const document of record.documents) {
      if (live.has(document.personnelDocumentTypeId)) continue;
      if (!document.isActive && !showDeleted) continue;

      seen.set(document.personnelDocumentTypeId, document.documentTypeName);
    }

    return [...seen].map(([id, name]) => ({ id, name }));
  }, [checklist, record.documents, showDeleted]);

  async function download(document: PersonnelDocument) {
    setBusyId(document.id);

    try {
      const { blob } = await personnelApi.downloadDocument(record.id, document.id);

      saveBlob(blob, document.fileName);
    } catch (caught) {
      toast.failure(
        caught instanceof ApiError ? caught.message : 'Could not download that document.',
      );
    } finally {
      setBusyId(null);
    }
  }

  async function remove(document: PersonnelDocument) {
    setBusyId(document.id);

    try {
      await personnelApi.deleteDocument(record.id, document.id);
      toast.success(`${document.fileName} was deleted.`);
      onChanged();
    } catch (caught) {
      toast.failure(
        caught instanceof ApiError ? caught.message : 'Could not delete that document.',
      );
    } finally {
      setBusyId(null);
    }
  }

  async function restore(document: PersonnelDocument) {
    setBusyId(document.id);

    try {
      await personnelApi.restoreDocument(record.id, document.id);
      toast.success(`${document.fileName} was restored.`);
      onChanged();
    } catch (caught) {
      toast.failure(
        caught instanceof ApiError ? caught.message : 'Could not restore that document.',
      );
    } finally {
      setBusyId(null);
    }
  }

  function row(
    type: { id: string; name: string; description?: string | null },
    uploadable: boolean,
  ): PreviewRow {
    const filed = byType.get(type.id) ?? [];

    return {
      label: type.name,
      value: (
        <div className="flex min-w-0 flex-col gap-1.5">
          {/*
            The Upload glyph takes the OUTER RIGHT, in the same column as every file's own
            delete beneath it — one gutter of actions down the row.
          */}
          {/*
            Hidden, not disabled, from a reader who cannot write the record — and the line
            drops with it when there is no description, rather than leaving an empty gap.
          */}
          {type.description || (uploadable && canUpdate) ? (
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 text-xs text-slate-400">{type.description}</span>

              {uploadable && canUpdate ? (
                <Tooltip
                  label={
                    record.isActive
                      ? 'Upload'
                      : 'This employee is deleted. Restore them before filing a document.'
                  }
                >
                  <button
                    type="button"
                    id={`personnel-document-upload-${type.id}`}
                    onClick={() => onUpload(type as PersonnelDocumentType)}
                    disabled={!record.isActive}
                    aria-label={`Upload ${type.name}`}
                    className={GLYPH_BUTTON + ' hover:text-slate-700'}
                  >
                    <Upload className="h-4 w-4" aria-hidden="true" />
                  </button>
                </Tooltip>
              ) : null}
            </div>
          ) : null}

          {filed.length === 0 ? (
            /*
              Not an em dash: the record has no document of this kind, which is a different
              fact from a field that does not apply — and it is the one the reader is
              scanning the checklist for.
            */
            <span className="text-slate-400">Not submitted</span>
          ) : (
            filed.map((document) => (
              <FiledDocumentLine
                key={document.id}
                document={document}
                busy={busyId === document.id}
                canWrite={record.isActive}
                onOpen={() => setPreviewing(document)}
                onReplace={() => onReplace(document)}
                onRemove={() => void remove(document)}
                onRestore={() => void restore(document)}
              />
            ))
          )}
        </div>
      ),
    };
  }

  const rows: PreviewRow[] = [
    ...(checklist ?? []).map((type) => row(type, true)),

    /*
      A retired kind takes NO Upload: it is not on the checklist any more, so offering to file
      against it would be a control that writes a row the server refuses.
    */
    ...retired.map((type) => row(type, false)),
  ];

  return (
    <ProfileSection
      actions={
        hasDeleted ? (
          <label
            htmlFor="personnel-documents-show-deleted"
            className="flex items-center gap-2 text-sm text-slate-600"
          >
            <Checkbox
              id="personnel-documents-show-deleted"
              checked={showDeleted}
              onChange={(event) => setShowDeleted(event.target.checked)}
            />
            Show deleted
          </label>
        ) : undefined
      }
    >
      {/* The four states, in the order they resolve. */}
      {error ? (
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      ) : checklist === null ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : rows.length === 0 ? (
        /*
          The one empty case left, and it is about the CHECKLIST rather than this record: with
          nothing on it there is nothing to file, and the fix is on another page.
        */
        <EmptyState message="Nothing on the document checklist. What a 201 file is expected to hold is maintained under Personnel → Settings → Documents." />
      ) : (
        <PreviewTable variant="table-display" rows={rows} />
      )}

      {previewing ? (
        <DocumentPreviewModal
          personnelId={record.id}
          document={previewing}
          onDownload={() => void download(previewing)}
          onClose={() => setPreviewing(null)}
        />
      ) : null}
    </ProfileSection>
  );
}

/**
 * A file's own action glyph, at the **32px floor a table's action gutter takes** — `p-2` around
 * a 16px icon.
 *
 * Below §5's 40px, deliberately and for that gutter's reason: these sit on a line of filenames
 * inside one checklist row, and a 40px control there is taller than the line it acts on. It is
 * NOT a third size — it is the same 32px `<Button size="sm">` already takes in a row's gutter,
 * and the same figure Repository's glyph quick actions take. A bare 16px icon, which this was
 * first written as, is below every one of those and is simply too small to hit.
 */
const GLYPH_BUTTON =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded text-slate-400 ' +
  'hover:bg-slate-100 disabled:text-slate-300 disabled:hover:bg-transparent';

/**
 * One filed document: its name, and what can be done to it.
 *
 * The name OPENS the preview — a FETCH re-typed to the verified type, never the file served
 * inline, which is what keeps a scanned certificate from becoming stored XSS. A DELETED
 * document's name is plain text: its bytes are not served until it is restored.
 */
function FiledDocumentLine({
  document,
  busy,
  canWrite,
  onOpen,
  onReplace,
  onRemove,
  onRestore,
}: {
  document: PersonnelDocument;
  busy: boolean;
  canWrite: boolean;
  onOpen: () => void;
  onReplace: () => void;
  onRemove: () => void;
  onRestore: () => void;
}) {
  /*
    Disabling is for STATE, and then it must say WHY — the tooltip carries the reason in place
    of the verb rather than beside it, because a disabled control's only question is why.
  */
  const deletedRecordReason = canWrite
    ? null
    : 'This employee is deleted. Restore them before changing their documents.';

  return (
    <span className="group/line flex min-w-0 items-center gap-2">
      <Paperclip className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />

      {/*
        The WRAPPER takes `flex-1`, pushing the action glyphs to the row's OUTER RIGHT under the
        Upload — on the button it would make the blank run after the name a click target.
      */}
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {document.isActive ? (
          <button
            type="button"
            onClick={onOpen}
            disabled={busy}
            className="min-w-0 truncate text-left font-medium text-slate-800 hover:underline disabled:text-slate-400"
          >
            {document.fileName}
          </button>
        ) : (
          <span className="min-w-0 truncate text-slate-400">{document.fileName}</span>
        )}

        {/* A deleted document says so where it is read, rather than only in the toggle above. */}
        {document.isActive ? null : (
          <span className="shrink-0 text-xs text-slate-400">Deleted</span>
        )}
      </span>

      {/*
        Revealed on the LINE's hover and on focus within — `opacity-0`, never `hidden`, so a
        keyboard reader tabs onto them and the names never shift. A device with no hover keeps
        them painted, or they could never be found there.
      */}
      <PermissionGate permission="personnel.update">
        <span className="flex shrink-0 items-center opacity-0 focus-within:opacity-100 group-hover/line:opacity-100 [@media(hover:none)]:opacity-100">
          {document.isActive ? (
            <>
              <Tooltip label={deletedRecordReason ?? 'Replace'}>
                <button
                  type="button"
                  onClick={onReplace}
                  disabled={busy || !canWrite}
                  aria-label={`Replace ${document.fileName}`}
                  className={GLYPH_BUTTON + ' hover:text-slate-700'}
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>

              <Tooltip label={deletedRecordReason ?? 'Delete'}>
                <button
                  type="button"
                  onClick={onRemove}
                  disabled={busy || !canWrite}
                  aria-label={`Delete ${document.fileName}`}
                  className={GLYPH_BUTTON + ' hover:text-red-600'}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            </>
          ) : (
            <Tooltip label={deletedRecordReason ?? 'Restore'}>
              <button
                type="button"
                onClick={onRestore}
                disabled={busy || !canWrite}
                aria-label={`Restore ${document.fileName}`}
                className={GLYPH_BUTTON + ' hover:text-slate-700'}
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
              </button>
            </Tooltip>
          )}
        </span>
      </PermissionGate>
    </span>
  );
}
